import { Button } from "primereact/button";
import { Dialog } from "primereact/dialog";
import { Dropdown } from "primereact/dropdown";
import { Editor } from 'primereact/editor';
import { Toast } from 'primereact/toast';
import { useEffect, useRef, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import ReCAPTCHA from 'react-google-recaptcha';
import { CONTACT_US_CATEGORY_OPTIONS, CONTACT_US_TYPE, CONTACT_US_TYPE_OPTIONS } from "../constants/contact-us";
import { REGEX } from "../constants/regex";
import { getCurrentUserFromLocalStorage, getI18nLanguageFromLocalStorage } from '../helpers/localStorageHelper';
import { translate } from "../localization/translateService";
import { postContactUs } from "../services/contactUsService";
import { uploadFileInChunks } from "../services/uploadFileService";
import assets from "../stories/assets/assets.png";
import { SuccessTemplate } from "./common/ToastMessage/Template";
import LoadingButton from "./common/LoadingButton";

const SUBJECT_MIN_LENGTH = 10;
const SUBJECT_MAX_LENGTH = 30;
const MESSAGE_MAX_LENGTH = 500;
const MESSAGE_MIN_LENGTH = 10;

export const LeaveMessagePopup = ({ visible, onHide, popupProps, invalidMessages, initialTypeCode = null }) => {
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [uploadedFile, setUploadedFile] = useState(null);
  const [showPreview, setShowPreview] = useState(false);
  const [finalFilePath, setFinalFilePath] = useState("");
  const [isDragActive, setIsDragActive] = useState(false);
  const uploadSubscriptionRef = useRef(null);
  const [typeOptions, setTypeOptions] = useState([]);
  const [categoryOptions, setCategoryOptions] = useState([]);
  const toast = useRef(null);
  const lang = getI18nLanguageFromLocalStorage();
  const recaptchaRef = useRef(null);
  const [captchaToken, setCaptchaToken] = useState(null);
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [messageText, setMessageText] = useState("");
  const emailRegex = REGEX.EMAIL;

  const {
    control,
    handleSubmit,
    reset,
    setValue,
    watch,
    trigger,
    formState: { errors },
  } = useForm({
    mode: "onChange",
    defaultValues: {
      type: null,
      category: null,
      name: "",
      email: "",
      subject: "",
      message: "",
      captcha: null,
    }
  });
  useEffect(() => {
    if (visible) {
      resetForm();
      loadAllTranslations();
    }
  }, [visible]);

  useEffect(() => {
    const currentUser = getCurrentUserFromLocalStorage();
    if (currentUser) {
      setIsLoggedIn(true);
      setValue("email", currentUser.email);
      setValue("name", currentUser.displayName);
    }

    return () => {
      if (uploadSubscriptionRef.current) {
        uploadSubscriptionRef.current.unsubscribe();
      }
    };
  }, [setValue]);

  useEffect(() => {
    const observer = new MutationObserver(() => {
      const arrow = document.querySelector('.g-recaptcha-bubble-arrow');
      if (arrow && arrow.parentElement) {
        arrow.parentElement.style.zIndex = 2147483647;
      }
    });
  
    observer.observe(document.body, { childList: true, subtree: true });
  
    return () => observer.disconnect();
  }, []);
  

  const loadAllTranslations = async () => {
    try {
      const typeOpts = await buildTranslatedOptions(CONTACT_US_TYPE_OPTIONS);
      const categoryOpts = await buildTranslatedOptions(CONTACT_US_CATEGORY_OPTIONS);

      setTypeOptions(typeOpts);
      setCategoryOptions(categoryOpts);
      const defaultCode = initialTypeCode || CONTACT_US_TYPE.LEARNER;
      const defaultSenderType = typeOpts.find(opt => opt.code === defaultCode);
      if (defaultSenderType) {
        setValue("type", defaultSenderType);
      }
    } catch { }
  };

  const buildTranslatedOptions = async (options) => {
    const newOptions = [];
    for (const item of options) {
      const translatedLabel = await translate(item.labelKey);
      newOptions.push({
        ...item,
        label: translatedLabel
      });
    }
    return newOptions;
  };

  const resetForm = () => {
    reset({
      type: null,
      category: null,
      name: isLoggedIn ? watch("name") : "",
      email: isLoggedIn ? watch("email") : "",
      subject: "",
      message: "",
      captcha: null,
    });
    setMessageText("");
    setUploading(false);
    setUploadProgress(0);
    setUploadedFile(null);
    setShowPreview(false);
    setFinalFilePath("");
    setIsDragActive(false);
    setCaptchaToken(null);
    if (recaptchaRef.current) {
      recaptchaRef.current.reset();
    }
  };

  const getFormErrorMessage = (name) => {
    return (
      errors[name] && <small className="p-error">{errors[name].message}</small>
    );
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.dataTransfer?.items?.length) {
      setIsDragActive(true);
    }
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragActive(false);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragActive(false);
    const file = e.dataTransfer?.files?.[0];
    if (!file) return;
    uploadSelectedFile(file);
  };

  const uploadSelectedFile = (file) => {
    setUploading(true);
    setUploadProgress(0);
    setShowPreview(false);
    setFinalFilePath("");
    const upload$ = uploadFileInChunks(file, 5, true);
    setUploadedFile({
      fileName: file.name,
      fileSize: `${(file.size / 1024 / 1024).toFixed(2)} MB`,
    });
    const sub = upload$.subscribe({
      next: (data) => {
        setUploadProgress(data.progress);
        if (data.progress === 100 && data.response) {
          const { filePath } = data.response;
          if (filePath) {
            setFinalFilePath(filePath);
          }
          setUploading(false);
          setShowPreview(true);
          const previewUrl = URL.createObjectURL(file);
          setUploadedFile({
            fileName: file.name,
            fileSize: `${(file.size / 1024 / 1024).toFixed(2)} MB`,
            previewUrl,
          });
        }
      },
      error: () => {
        setUploading(false);
      },
      complete: () => { },
    });
    uploadSubscriptionRef.current = sub;
  };

  const getTextContentLength = () => {
    return messageText?.length ?? 0;
  };

  const handleReplaceFile = () => {
    setUploadedFile(null);
    setShowPreview(false);
    setFinalFilePath("");
    setUploading(false);
    setUploadProgress(0);
  };

  const handleFileSelect = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    uploadSelectedFile(file);
  };

  const scrollToElement = (element) => {
    if (element) {
      element.scrollIntoView({
        behavior: "smooth",
        block: "center"
      });

      element.focus();
      return;
    }
  }

  const scrollDialogToTop = () => {
    const invalidInput = document.querySelector('.p-invalid');

    if (invalidInput) {
      scrollToElement(invalidInput);
      return;
    }

    const invalidCapcha = document.querySelector('.keduall-recaptcha .p-error');
    if (invalidCapcha) {
      scrollToElement(invalidCapcha);
      return;
    }

    const dialogContent = document.querySelector('.leave-msg-dialog .p-dialog-content');
    if (dialogContent) {
      scrollToElement(dialogContent);
      return;
    }
  };

  const onSubmit = async (data) => {
    if (isSubmitting) return;
    
    setIsSubmitting(true);
    
    const payload = {
      subject: data.subject.trim(),
      message: data.message,
      email: data.email.trim(),
      name: data.name.trim(),
      attachment: finalFilePath || "",
      type: data.type?.code || "",
      category: data.category?.code || "",
    };

    try {
      const resp = await postContactUs(payload);
      if (resp?.id) {
        resetForm();
        onHide();
        toast.current.show({
          severity: 'success',
          detail: invalidMessages.successToastMessage,
          life: 3000,
          className: 'keduall-toast',
          sticky: false,
          content: (props) => SuccessTemplate(props)
        });
      } else {
        // Handle error
      }
    } catch {
      // Handle error
    }
    setIsSubmitting(false);
  };

  const onSubmitWithValidation = handleSubmit(
    onSubmit,
    (errors) => {
      scrollDialogToTop();
      setIsSubmitting(false);
    }
  );

  const handleMessageChange = (e, field) => {
    if (e.textValue.trim().length > MESSAGE_MAX_LENGTH) return;
    setMessageText(e.textValue.trim());
    field.onChange(e.htmlValue);
  };

  const handleImageError = (e) => {
    e.target.src = assets;
  };

  const dialogFooter = (
    <div className="leave-msg-dialog-footer border-radius-125 p-125 flex justify-content-end">
      <LoadingButton
        onClick={onSubmitWithValidation}
        type="submit"
        form="leave-message-form"
        label={popupProps.submitLabel}
        loading={isSubmitting}
        className="keduall-buttons button-filled-default-normal-primary text-white-txt"
      />
    </div>
  );

  return (
    <div>
      <Dialog
        visible={visible}
        onHide={onHide}
        className="leave-msg-dialog border-radius-125 w-full md:w-5"
        header={null}
        headerClassName="border-radius-125 p-3"
        footerClassName="border-radius-125"
        contentClassName="gap-2 flex flex-column w-full p-125 keduall-scrollbar"
        footer={dialogFooter}
        modal
        draggable={false}
        blockScroll={true}
        baseZIndex="10001"
      >
        <div className="leave-msg-header">{popupProps.title}</div>
        
        <form id="leave-message-form">
          <div className="flex gap-075 flex-wrap md:flex-nowrap">
            <div className="flex flex-column gap-0125 w-full md:w-6">
              <label className="keduall-typography text-16-normal-400 text-default-txt flex-column gap-2">
                {popupProps.categoryLabel} <small className="p-error">*</small>
              </label>
              <Controller
                name="category"
                control={control}
                rules={{
                  required: invalidMessages.errorPleaseFillRequired,
                }}
                render={({ field, fieldState }) => (
                  <Dropdown
                    {...field}
                    options={categoryOptions}
                    optionLabel="label"
                    placeholder={popupProps.categoryPlaceholder}
                    className={`${fieldState.error ? "p-invalid" : ""} w-full keduall-form input-default-default keduall-typography text-16-normal-400 text-default-txt`}
                    appendTo={"self"}
                  />
                )}
              />
              {getFormErrorMessage("category")}
            </div>
            
            <div className="flex flex-column gap-0125 w-full md:w-6">
              <label className="keduall-typography text-16-normal-400 text-default-txt flex-column gap-2">
                {popupProps.typeLabel} <small className="p-error">*</small>
              </label>
              <Controller
                name="type"
                control={control}
                rules={{
                  required: invalidMessages.errorPleaseFillRequired,
                }}
                render={({ field, fieldState }) => (
                  <Dropdown
                    {...field}
                    options={typeOptions}
                    optionLabel="label"
                    placeholder={popupProps.typePlaceholder}
                    className={`${fieldState.error ? "p-invalid" : ""} w-full keduall-form input-default-default keduall-typography text-16-normal-400 text-default-txt`}
                    appendTo={"self"}
                  />
                )}
              />
              {getFormErrorMessage("type")}
            </div>
          </div>
          
          {!isLoggedIn && (
            <div className="flex gap-075 flex-wrap md:flex-nowrap">
              <div className="flex flex-column gap-0125 w-full md:w-6">
                <label htmlFor="name-input" className="keduall-typography text-16-normal-400 text-default-txt flex-column gap-2">
                  {popupProps.nameLabel} <small className="p-error">*</small>
                </label>
                <Controller
                  name="name"
                  control={control}
                  rules={{
                    required: invalidMessages.errorPleaseFillRequired,
                  }}
                  render={({ field, fieldState }) => (
                    <input
                      {...field}
                      id="name-input"
                      type="text"
                      className={`${fieldState.error ? "p-invalid" : ""} w-full keduall-form input-default-default keduall-typography text-16-normal-400 text-default-txt`}
                      placeholder={popupProps.namePlaceholder}
                    />
                  )}
                />
                {getFormErrorMessage("name")}
              </div>
              
              <div className="flex flex-column gap-0125 w-full md:w-6">
                <label htmlFor="email-input" className="keduall-typography text-16-normal-400 text-default-txt flex-column gap-2">
                  {popupProps.emailLabel} <small className="p-error">*</small>
                </label>
                <Controller
                  name="email"
                  control={control}
                  rules={{
                    required: invalidMessages.errorPleaseFillRequired,
                    pattern: {
                      value: emailRegex,
                      message: invalidMessages.errorInvalidEmail,
                    },
                  }}
                  render={({ field, fieldState }) => (
                    <input
                      {...field}
                      id="email-input"
                      type="email"
                      className={`${fieldState.error ? "p-invalid" : ""} w-full keduall-form input-default-default keduall-typography text-16-normal-400 text-default-txt`}
                      placeholder={popupProps.emailPlaceholder}
                    />
                  )}
                />
                {getFormErrorMessage("email")}
              </div>
            </div>
          )}

          <div className="flex flex-column gap-0125 w-full">
            <label htmlFor="subject-input" className="keduall-typography text-16-normal-400 text-default-txt flex-column gap-2">
              {popupProps.subjectLabel} <small className="p-error">*</small>
            </label>
            <Controller
              name="subject"
              control={control}
              rules={{
                required: invalidMessages.errorPleaseFillRequired,
                minLength: {
                  value: SUBJECT_MIN_LENGTH,
                  message: invalidMessages.errorMessageTooShort,
                },
                maxLength: {
                  value: SUBJECT_MAX_LENGTH,
                  message: `Maximum length is ${SUBJECT_MAX_LENGTH} characters`,
                },
              }}
              render={({ field, fieldState }) => (
                <input
                  {...field}
                  id="subject-input"
                  type="text"
                  className={`${fieldState.error ? "p-invalid" : ""} keduall-form input-default-default keduall-typography text-16-normal-400 text-default-txt`}
                  placeholder={popupProps.subjectPlaceholder}
                  maxLength={SUBJECT_MAX_LENGTH}
                />
              )}
            />
            {getFormErrorMessage("subject")}
          </div>

          <div className="flex flex-column gap-0125 w-full">
            <label className="keduall-typography text-16-normal-400 text-default-txt flex-column gap-2">
              {popupProps.messageLabel || "Message"} <small className="p-error">*</small>
            </label>
            <Controller
              name="message"
              control={control}
              rules={{
                required: invalidMessages.errorPleaseFillRequired,
                validate: {
                  minLength: () => {
                    if (messageText.length < MESSAGE_MIN_LENGTH) {
                      return invalidMessages.errorMessageTooShort;
                    }
                    return true;
                  },
                },
              }}
              render={({ field, fieldState }) => (
                <Editor
                  {...field}
                  placeholder={popupProps.messagePlaceholder}
                  className={`flex flex-column keduall-editor ${fieldState.error ? "p-invalid" : ""}`}
                  onTextChange={(e) => handleMessageChange(e, field)}
                  maxLength={MESSAGE_MAX_LENGTH}
                />
              )}
            />
            {getFormErrorMessage("message")}
            <div className="flex justify-content-end">
              <small className={`keduall-typography text-12-normal-400 ${getTextContentLength() > MESSAGE_MAX_LENGTH ? "text-danger-txt" : "text-secondary-txt"}`}>
                {getTextContentLength()} / {MESSAGE_MAX_LENGTH}
              </small>
            </div>
          </div>

          <div
            className={`${!showPreview && !(uploadedFile?.previewUrl) ? "leave-msg-attach" : ""} flex ${isDragActive ? "drag-active" : ""}`}
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            onDrop={handleDrop}
          >
            {!uploading && !showPreview && (
              <div className="flex flex-column gap-2 p-4 w-full justify-content-center align-items-center relative">
                <input
                  className="absolute top-0 left-0 w-full h-full z-3 opacity-0 cursor-pointer"
                  type="file"
                  onChange={handleFileSelect}
                />
                <div className="keduall-typography text-16-medium-500 text-default-txt">{popupProps.uploadFileLabel}</div>
                <div className="keduall-typography text-14-normal-400 text-default-txt text-third-txt">
                  {popupProps.uploadFileDescription}
                </div>
              </div>
            )}
            {uploading && !showPreview && (
              <div className="flex flex-column gap-3 px-4 py-1625 w-full">
                <div className="flex flex-row justify-content-between w-full flex-wrap md:flex-nowrap">
                  <span className="keduall-typography text-16-medium-500 text-default-txt">{popupProps.loadingLabel}</span>
                  <span className="keduall-typography text-14-normal-400 text-secondary-txt gap-1 flex flex-row">
                    <span className="pi pi-paperclip flex"></span>
                    <span className="flex">{uploadedFile?.fileSize}</span>
                  </span>
                </div>
                <div className="leave-msg-progressbar">
                  <div
                    className="leave-msg-progressbar-fill"
                    style={{ width: `${uploadProgress}%` }}
                  />
                </div>
              </div>
            )}
            {showPreview && uploadedFile?.previewUrl && (
              <div className="w-full flex flex-row justify-content-start gap-0875 flex-wrap md:flex-nowrap">
                <img
                  src={uploadedFile.previewUrl}
                  alt="thumbnail"
                  className="w-full flex max-h-10rem max-w-10rem min-w-10rem"
                  onError={handleImageError}
                />
                <div className="leave-msg-attach-preview-info gap-2">
                  <div className="keduall-typography text-14-normal-400 text-default-txt">{uploadedFile.fileName}</div>
                  <div className="keduall-buttons button-outlined-small-secondary w-max" onClick={handleReplaceFile}>
                    {popupProps.changeFileLabel}
                  </div>
                </div>
              </div>
            )}
          </div>
          
          <div className="keduall-recaptcha mt-2">
            <Controller
              name="captcha"
              control={control}
              rules={{
                required: invalidMessages.errorCaptchaInvalid,
              }}
              render={({ field, fieldState }) => (
                <>
                  <ReCAPTCHA
                    hl={lang}
                    ref={recaptchaRef}
                    sitekey={popupProps.capchaSiteKey}
                    onChange={(token) => {
                      setCaptchaToken(token);
                      field.onChange(token);
                    }}
                    onExpired={() => {
                      setCaptchaToken(null);
                      field.onChange(null);
                    }}
                  />
                  {fieldState.error && (
                    <small className="block p-error">{fieldState.error.message}</small>
                  )}
                </>
              )}
            />
          </div>
        </form>

      </Dialog>
      <Toast ref={toast} position="bottom-right" className="keduall-toast" />
    </div>
  );
};